import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DaycareService } from '../services/daycareService';
import { transformPackageFormData, filterValidPackages } from '../utils/daycareUtils';

/**
 * Custom hook for managing daycare package operations
 * Encapsulates package-related state and business logic
 */
export function useDaycarePackages() {
    const { authorizedFetch } = useAuth();
    
    // State
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    /**
     * Load all active packages from API
     */
    const loadActivePackages = async () => {
        setLoading(true);
        setError('');
        
        try {
            const packagesData = await DaycareService.getActivePackages(authorizedFetch);
            const validPackages = filterValidPackages(packagesData);
            setPackages(validPackages);
        } catch (err) {
            console.error('Failed to load active packages:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Create a new daycare package
     * @param {Object} formData - Package form data { dogId, packageType }
     */
    const createPackage = async (formData) => {
        setError('');
        setSuccess('');
        
        if (!formData.dogId) {
            setError('Please select a dog');
            throw new Error('Dog selection required');
        }
        
        try {
            const packageData = transformPackageFormData(formData);
            const createdPackage = await DaycareService.createPackage(packageData, authorizedFetch);
            
            setSuccess('Package purchased successfully!');
            await loadActivePackages(); // Refresh the list
            
            return createdPackage;
        } catch (err) {
            console.error('Failed to create package:', err);
            setError(err.message);
            throw err;
        }
    };

    /**
     * Update an existing package
     * @param {string} packageId - Package ID
     * @param {Object} packageData - Updated package data
     */
    const updatePackage = async (packageId, packageData) => {
        setError('');
        setSuccess('');
        
        try {
            const updatedPackage = await DaycareService.updatePackage(packageId, packageData, authorizedFetch);
            
            setSuccess('Package updated successfully!');
            await loadActivePackages(); // Refresh the list
            
            return updatedPackage;
        } catch (err) {
            console.error('Failed to update package:', err);
            setError(err.message);
            throw err;
        }
    };

    /**
     * Delete a package
     * @param {string} packageId - Package ID
     */
    const deletePackage = async (packageId) => {
        setError('');
        setSuccess('');
        
        try {
            await DaycareService.deletePackage(packageId, authorizedFetch);
            
            // Remove from local state immediately for better UX
            setPackages(prev => prev.filter(pkg => pkg._id !== packageId));
            setSuccess('Package deleted successfully!');
        } catch (err) {
            console.error('Failed to delete package:', err);
            setError(err.message);
            throw err;
        }
    };

    const cancelPackage = async (packageId, note = '') => {
        setError('');
        setSuccess('');
        try {
            await DaycareService.cancelPackage(packageId, authorizedFetch, note);
            setPackages(prev => prev.filter(pkg => pkg._id !== packageId));
            setSuccess('Package cancelled.');
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    /**
     * Clear messages
     */
    const clearMessages = () => {
        setError('');
        setSuccess('');
    };

    /**
     * Initialize data loading
     */
    useEffect(() => {
        loadActivePackages();
    }, []);

    return {
        packages,
        loading,
        error,
        success,
        loadActivePackages,
        createPackage,
        updatePackage,
        deletePackage,
        cancelPackage,
        clearMessages
    };
}
